<template>
	<div class="pi-button-group">
		<!-- 直接展示的按钮 -->
		<slot :row="row" :index="index"></slot>

		<!-- 更多按钮（作为弹层锚点） -->
		<div v-if="hasMore" ref="anchorRef" class="pi-button-group__anchor">
			<el-button text size="small" type="primary" @click.stop="toggle">
				{{ text }}<el-icon class="el-icon--right"><el-icon-arrow-down/></el-icon>
			</el-button>
		</div>

		<!-- 自定义弹层：v-show 仅隐藏不卸载，保证内部 popconfirm 等组件及其浮层在关闭后仍存活 -->
		<teleport to="body">
			<transition name="pi-menu">
				<div v-if="visible" ref="menuRef" class="pi-button-group__menu"
					 :class="{ 'pi-button-group__menu--up': pos.arrowDown }" :style="menuStyle"
					 @click="onMenuClick">
					<slot name="more" :row="row" :index="index"></slot>
				</div>
			</transition>
		</teleport>
	</div>
</template>

<script setup>
import {computed, nextTick, onBeforeUnmount, onMounted, ref, useSlots} from 'vue'

const props = defineProps({
	// 当前行数据（通过 scoped slot 提供给插槽内容）
	row: {type: Object, default: null},
	// 当前行索引
	index: {type: Number, default: 0},
	// 更多按钮文字
	text: {type: String, default: '更多'},
	// 弹层与按钮的垂直间距
	offset: {type: Number, default: 4},
})

const slots = useSlots()
const hasMore = !!slots.more

const anchorRef = ref(null)
const menuRef = ref(null)
const visible = ref(false)
const pos = ref({top: 0, right: 0, minWidth: 0})

const menuStyle = computed(() => ({
	top: pos.value.top + 'px',
	right: pos.value.right + 'px',
	minWidth: pos.value.minWidth + 'px',
	'--arrow-right': pos.value.arrowRight + 'px',
}))

// 弹层右缘对齐锚点右缘；默认向下展开，下方空间不足时自动向上展开；
// 箭头水平居中于锚点；宽度随内容自适应且不窄于锚点；避免超出视口边缘
function updatePos() {
	const rect = anchorRef.value?.getBoundingClientRect()
	if (!rect) return
	const offset = props.offset
	const menuHeight = menuRef.value?.offsetHeight ?? 0
	// 下方可用空间不足以容纳弹层时，改为向上展开
	const spaceBelow = window.innerHeight - rect.bottom - offset
	const placeAbove = menuHeight > 0 && spaceBelow < menuHeight + offset
	const top = placeAbove
		? Math.max(offset, rect.top - menuHeight - offset)
		: rect.bottom + offset
	pos.value = {
		top,
		right: Math.max(8, window.innerWidth - rect.right),
		minWidth: rect.width,
		// 弹层右缘与锚点右缘对齐，箭头中心即锚点中心，距弹层右缘 = 锚点宽一半
		arrowRight: rect.width / 2,
		arrowDown: placeAbove,
	}
}

function toggle() {
	visible.value = !visible.value
	if (visible.value) {
		nextTick(updatePos)
	}
}

function hide() {
	visible.value = false
}

// 点击弹层内任意位置：延迟判断是否触发了确认框（el-popconfirm），
// 有则保持菜单打开（确认流程中不关闭"更多"），无则关闭菜单
function onMenuClick() {
	window.setTimeout(() => {
		if (!hasOpenPopconfirm()) hide()
	}, 200)
}

// 是否有处于打开状态的 el-popconfirm 确认框（popper 浮层挂在 body 上）
// 注意：页面可能同时存在多个 el-popper 浮层（如表格列设置），须遍历找出可见且含确认框内容的那个
function hasOpenPopconfirm() {
	const poppers = document.querySelectorAll('.el-popper')
	for (let i = 0; i < poppers.length; i++) {
		const p = poppers[i]
		if (p.style.display !== 'none' && p.querySelector('.el-popconfirm')) return true
	}
	return false
}

// 点击外部关闭（含 body 上的 popconfirm 确认框）
function onDocumentClick(e) {
	// 点击确认框浮层任意区域（含确定/取消按钮）：保持菜单打开，不关闭"更多"
	if (e.target.closest('.el-popconfirm')) return
	const anchor = anchorRef.value
	if (anchor?.contains(e.target)) return
	if (menuRef.value?.contains(e.target)) return
	hide()
}

// 表格滚动 / 窗口变化时关闭，避免弹层错位
function onScroll() {
	if (visible.value) hide()
}

onMounted(() => {
	document.addEventListener('click', onDocumentClick, true)
	document.addEventListener('scroll', onScroll, true)
	window.addEventListener('resize', onScroll)
})

onBeforeUnmount(() => {
	document.removeEventListener('click', onDocumentClick, true)
	document.removeEventListener('scroll', onScroll, true)
	window.removeEventListener('resize', onScroll)
})
</script>

<style scoped>
.pi-button-group {
	display: inline-flex;
	align-items: center;
	white-space: nowrap;
}

.pi-button-group :deep(.el-button + .el-button) {
	margin-left: 8px;
}

.pi-button-group__anchor {
	display: inline-flex;
	align-items: center;
}

/* 弹层容器：与 el-dropdown 菜单一致 */
.pi-button-group__menu {
	position: fixed;
	z-index: 3000;
	width: max-content;
	padding: 8px 4px;
	background: var(--el-bg-color-overlay, #fff);
	border: 1px solid var(--el-border-color-light, #e4e7ed);
	border-radius: 6px;
	box-shadow: var(--el-box-shadow-light, 0 2px 12px rgba(0, 0, 0, .1));
	display: flex;
	flex-direction: column;
	gap: 4px;
}

/* 弹层内按钮：撑满弹层宽度，文字左对齐；颜色等样式保持原生 */
.pi-button-group__menu :deep(.el-button) {
	width: 100%;
	margin-left: 0;
	justify-content: flex-start;
}

.pi-button-group__menu :deep(.el-button + .el-button) {
	margin-left: 0;
}

/* 小箭头：突出在弹层上方、水平居中指向"更多"按钮 */
.pi-button-group__menu::before,
.pi-button-group__menu::after {
	content: '';
	position: absolute;
	right: var(--arrow-right, 16px);
	transform: translateX(50%);
	border: 6px solid transparent;
	border-bottom-color: var(--el-border-color-light, #e4e7ed);
}

/* 灰描边箭头：底边贴弹层顶部，尖端突出在弹层上方 */
.pi-button-group__menu::before {
	top: -12px;
}

/* 白填充箭头：覆盖灰色，四周留 1px 灰边 */
.pi-button-group__menu::after {
	top: -11px;
	border-width: 5px;
	border-bottom-color: var(--el-bg-color-overlay, #fff);
}

/* 向上展开：箭头移到弹层底部、尖端朝下指向锚点 */
.pi-button-group__menu--up::before {
	top: auto;
	bottom: -12px;
	border-bottom-color: transparent;
	border-top-color: var(--el-border-color-light, #e4e7ed);
}

.pi-button-group__menu--up::after {
	top: auto;
	bottom: -11px;
	border-bottom-color: transparent;
	border-top-color: var(--el-bg-color-overlay, #fff);
}

/* 过渡动画 */
.pi-menu-enter-active,
.pi-menu-leave-active {
	transition: opacity .15s ease, transform .15s ease;
}

.pi-menu-enter-from,
.pi-menu-leave-to {
	opacity: 0;
	transform: translateY(-4px);
}
</style>
